import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Phone, PhoneOff } from 'lucide-react';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { apiClient } from '@/lib/apiClient';
import { useAuthStore } from '@/stores/authStore';

interface QueueItem {
  id: string;
  leadId: string | null;
  priority: number;
  scheduledFor: string;
  status: string;
  leadName?: string;
  phone?: string;
}

interface DispositionState {
  callId: string;
  phone: string;
}

const DISPOSITIONS = [
  'CONNECTED',
  'VOICEMAIL',
  'NO_ANSWER',
  'BUSY',
  'WRONG_NUMBER',
  'DO_NOT_CALL',
] as const;

export function DialerPage() {
  const { t } = useTranslation();
  const userId = useAuthStore((s) => s.user?.userId);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [disposition, setDisposition] = useState<DispositionState | null>(null);
  const [selectedDisp, setSelectedDisp] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [dncBlocked, setDncBlocked] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!userId) return;
    loadQueue();
  }, [userId]);

  function loadQueue() {
    if (!userId) return;
    setLoading(true);
    apiClient
      .get(`/api/v1/dialer/queue?userId=${userId}`)
      .then((r) => setQueue(r.data))
      .catch(() => setQueue([]))
      .finally(() => setLoading(false));
  }

  async function checkDnc(phone: string): Promise<boolean> {
    try {
      const r = await apiClient.get(`/api/v1/dialer/dnc-check?phone=${encodeURIComponent(phone)}`);
      return r.data.blocked;
    } catch {
      return false;
    }
  }

  async function handleCall(item: QueueItem) {
    if (!item.phone || !userId) return;
    const blocked = await checkDnc(item.phone);
    if (blocked) {
      setDncBlocked((prev) => new Set([...prev, item.id]));
      showToast(t('dialer.call_blocked_dnc'));
      return;
    }
    try {
      const r = await apiClient.post('/api/v1/dialer/calls', {
        userId,
        recipientPhone: item.phone,
        leadId: item.leadId,
      });
      // Mark item as ACTIVE in queue
      await apiClient.put(`/api/v1/dialer/queue/${item.id}/status`, { status: 'ACTIVE' });
      setDisposition({ callId: r.data.callId, phone: item.phone });
      setSelectedDisp('');
      setQueue((prev) => prev.filter((q) => q.id !== item.id));
    } catch {
      showToast(t('dialer.call_blocked_dnc'));
    }
  }

  async function handleFileDisposition() {
    if (!disposition || !selectedDisp) return;
    setSaving(true);
    try {
      await apiClient.post(`/api/v1/dialer/calls/${disposition.callId}/disposition`, {
        outcome: selectedDisp,
      });
      setDisposition(null);
      showToast(t('common.saved'));
      loadQueue();
    } catch {
      showToast(t('common.error'));
    } finally {
      setSaving(false);
    }
  }

  async function handleSkip(item: QueueItem) {
    await apiClient
      .put(`/api/v1/dialer/queue/${item.id}/status`, { status: 'SKIPPED' })
      .catch(() => {});
    setQueue((prev) => prev.filter((q) => q.id !== item.id));
  }

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  return (
    <div className="space-y-6 p-6">
      <Breadcrumbs crumbs={[{ label: t('dialer.title'), to: '/dialer' }]} />
      <h1 className="text-2xl font-bold">{t('dialer.title')}</h1>

      {toast && (
        <div className="fixed top-4 right-4 z-50 rounded bg-foreground px-4 py-2 text-sm text-background shadow">
          {toast}
        </div>
      )}

      {/* Disposition modal overlay */}
      {disposition && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
          <Card className="w-full max-w-sm space-y-4 p-6">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <PhoneOff className="h-5 w-5" />
              <span>{t('dialer.disposition_label')}</span>
            </div>
            <p className="text-sm text-muted-foreground">{disposition.phone}</p>
            <div className="grid grid-cols-2 gap-2">
              {DISPOSITIONS.map((d) => (
                <button
                  key={d}
                  onClick={() => setSelectedDisp(d)}
                  className={`rounded border px-3 py-2 text-sm transition-colors ${
                    selectedDisp === d
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border hover:bg-muted'
                  }`}
                >
                  {t(`dialer.disposition_${d}`)}
                </button>
              ))}
            </div>
            <Button
              onClick={handleFileDisposition}
              disabled={!selectedDisp || saving}
              className="w-full"
            >
              {saving ? t('common.saving') : t('dialer.file_disposition')}
            </Button>
          </Card>
        </div>
      )}

      {/* Queue */}
      <Card className="space-y-4 p-6">
        <h2 className="text-lg font-semibold">{t('dialer.queue_title')}</h2>
        {loading ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : queue.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('dialer.queue_empty')}</p>
        ) : (
          <div className="divide-y divide-border">
            {queue.map((item) => {
              const blocked = dncBlocked.has(item.id);
              return (
                <div key={item.id} className="flex items-center justify-between py-3">
                  <div className="space-y-0.5">
                    <p className="font-medium">
                      {item.leadName ?? t('common.unknown')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {item.phone ?? '—'} &bull;{' '}
                      {t('dialer.priority')}: {item.priority}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleCall(item)}
                      disabled={blocked || !item.phone}
                      title={blocked ? t('dialer.call_blocked_dnc') : undefined}
                      className={blocked ? 'opacity-40' : ''}
                    >
                      <Phone className="mr-1 h-4 w-4" />
                      {t('dialer.start_call')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSkip(item)}
                    >
                      {t('common.skip')}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
